import React, { useMemo, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm, ValidationError } from '@formspree/react';
import calculateScore from '../form/calculateScore';
import schema from '../form/schema.json';
import nextSteps from '../form/nextSteps.json';
import AnimatedScoreDisplay from './AnimatedScoreDisplay';

function Results() {
	const location = useLocation();
	const navigate = useNavigate();
	const results = location.state?.results || {};
	const { total, min, max, breakdown, categoryBreakdown, score100 } = useMemo(() => calculateScore(results), [results]);
	const goBackToForm = () => navigate('/form');
	
	// Formspree form handling
	const [state, handleSubmit] = useForm("mrbaweyq");

	// Scroll to top when component mounts
	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);


	// Small reusable Advice card with See all toggle
	const labelMap = { '0-25': '0–25%', '25-50': '25–50%', '50-75': '50–75%', '75-100': '75–100%' };
	const AdviceCard = ({ title, band, mainText, allAdvice }) => {
		const [open, setOpen] = useState(false);
		return (
			<div className="card">
				<div className="flex justify-between items-start mb-2">
					<h3 className="text-lg font-semibold">{title} <span className="text-muted">({labelMap[band]})</span></h3>
					<button className="btn btn-ghost btn-sm" onClick={() => setOpen(!open)}>{open ? 'Hide all' : 'See all'}</button>
				</div>
				<p className="text-secondary">{mainText}</p>
				{open && (
					<div className="space-y-2 mt-3">
						{allAdvice.map(({ band: b, text }) => (
							<div key={b}>
								<p className="text-sm text-muted mb-1">{labelMap[b] || b}</p>
								<p className="text-secondary">{text}</p>
							</div>
						))}
					</div>
				)}
			</div>
		);
	};

	// Build category list dynamically from schema order and titles
	const categoryItems = useMemo(() => {
		const props = schema?.properties || {};
		const bandFor = (score) => {
			if (score <= 25) return '0-25';
			if (score <= 50) return '25-50';
			if (score <= 75) return '50-75';
			return '75-100';
		};
		return Object.keys(props).map((key) => {
			const title = props[key]?.title || key;
			const score = categoryBreakdown?.[key]?.score100 ?? 0;
			const band = bandFor(score);
			const advice = (nextSteps?.[key] && nextSteps[key][band]) || '';
			const bandsOrder = ['0-25','25-50','50-75','75-100'];
			const allRaw = nextSteps?.[key] || {};
			const allAdvice = bandsOrder.map((b) => ({ band: b, text: allRaw[b] || '' }));
			return { key, title, score, advice, band, allAdvice };
		});
	}, [categoryBreakdown]);

	const [expanded, setExpanded] = useState({});
	const toggleExpand = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

	// Overall guidance text from nextSteps based on overall score
	const bandForOverall = (s) => (s <= 25 ? '0-25' : s <= 50 ? '25-50' : s <= 75 ? '50-75' : '75-100');
	const overallBand = bandForOverall(score100);
	const overallAdvice = nextSteps?.overall?.[overallBand] || '';

	return (
		<div className="min-h-screen py-8">
			<div className="container">
				<div style={{ maxWidth: '900px', margin: '0 auto' }} className="fade-in">
					<div className="card card-lg">
						<div className="text-center mb-8">
							<h1 className="text-3xl font-semibold text-primary mb-6">Your Organization is:</h1>
							<div style={{ 
								height: '300px',
								marginBottom: '2rem',
								// background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1), rgba(6, 182, 212, 0.1), rgba(16, 185, 129, 0.1))',
								borderRadius: '16px',
								padding: '20px',
								position: 'relative',
								overflow: 'hidden',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center'
							}}>
								{/* Subtle background pattern */}
								<div style={{
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									// border:'1px solid red',
									// backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(124, 58, 237, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.05) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(16, 185, 129, 0.05) 0%, transparent 50%)',
									pointerEvents: 'none'
								}} />
								<AnimatedScoreDisplay score={score100} title="AI Ready" />
							</div>
						</div>
						{/* <div className="mb-6">
							<h2 className="text-xl font-semibold mb-2">Overall Guidance</h2>
							<AdviceCard
								title="Institution-wide Readiness"
								band={overallBand}
								mainText={overallAdvice}
								allAdvice={(['0-25','25-50','50-75','75-100']).map(b => ({ band: b, text: nextSteps?.overall?.[b] || '' }))}
							/>
						</div> */}
						<div className="mb-6">
							<h2 className="text-xl font-semibold mb-2">Category Breakdown</h2>
							<ul className="space-y-2">
								{categoryItems.map(({ key, title, score },index) => (
									<li key={key} className="flex items-center justify-between space-x-4 py-2">
										<span>{title}</span>
										<div className="flex items-center space-x-2">
											<div style={{ width: 60, height: 60 }}>
												<AnimatedScoreDisplay score={score } title="" fontSize="1.3rem" showWheel={false} animationOffset={index * 1000} />
											</div>
									
										</div>
									</li>
								))}

							</ul>
						</div>
						<div className="mb-6">
							<div className="card card-lg" style={{ 
								background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
								border: '2px solid rgba(99, 102, 241, 0.15)',
								boxShadow: '0 20px 40px rgba(99, 102, 241, 0.1), 0 10px 25px rgba(0, 0, 0, 0.1)'
							}}>
								<div className="text-center mb-8">
									<h2 className="text-3xl font-bold text-primary mb-4">Request Your Personalized Report</h2>
									<div className="max-w-2xl mx-auto space-y-4">
										<p className="text-secondary text-lg leading-relaxed">
											A <span className="font-semibold text-primary">real human SME at Beyond</span> will review your answers, and prepare a 
											<span className="font-semibold text-primary"> detailed narrative report</span> for you - along with insights on how to advance from where you are.
										</p>
										<div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
											<p className="text-secondary text-lg font-medium">
												Simply fill out the form below, and you'll have a report in your email box within 
												<span className="font-bold text-blue-700"> 2 business days</span> - often much sooner.
											</p>
										</div>
									</div>
								</div>
								
								<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-lg" style={{
									// boxShadow: '0 8px 20px rgba(0, 0, 0, 0.08)'
								}}>
								
{state.succeeded ? (
									<div className="text-center py-8">
										<h3 className="text-xl font-semibold text-green-600 mb-2">Thank you for your interest!</h3>
										<p className="text-secondary mb-4">
											We've received your information and will be in touch soon with personalized insights based on your AI readiness assessment.
										</p>
										<p className="text-sm text-gray-500">
											Check your email for a confirmation and expect to hear from our team within 1-2 business days.
										</p>
									</div>
								) : (
									<form onSubmit={handleSubmit} className="space-y-5">
										{/* Hidden field to include assessment score */}
										<input type="hidden" name="assessment_score" value={score100} />
										<input type="hidden" name="category_breakdown" value={JSON.stringify(categoryBreakdown)} />
										
										<div className="form-group">
											<label htmlFor="name" className="form-label block mb-2">
												<span className="text-sm font-bold text-gray-800">Your Name</span>
												<span className="text-red-500 ml-1 font-bold">*</span>
											</label>
											<div className="relative">
												<input
													type="text"
													id="name"
													name="name"
													required
													placeholder="Your full name"
													className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-3 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white ` hover:border-gray-300"
												/>
											</div>
											<ValidationError 
												prefix="Name" 
												field="name"
												errors={state.errors}
												className="text-red-500 text-sm mt-1"
											/>
										</div>
										
										<div className="form-group">
											<label htmlFor="email" className="form-label block mb-2">
												<span className="text-sm font-bold text-gray-800">Email Address</span>
												<span className="text-red-500 ml-1 font-bold">*</span>
											</label>
											<div className="relative">
												<input
													type="email"
													id="email"
													name="email"
													required
													placeholder="your.email@institution.edu"
													className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-3 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:border-gray-300"
												/>
											</div>
											<ValidationError 
												prefix="Email" 
												field="email"
												errors={state.errors}
												className="text-red-500 text-sm mt-1"
											/>
										</div>
										
										<div className="form-group">
											<label htmlFor="institution" className="form-label block mb-2">
												<span className="text-sm font-bold text-gray-800">Institution Name</span>
												<span className="text-red-500 ml-1 font-bold">*</span>
											</label>
											<div className="relative">
												<input
													type="text"
													id="institution"
													name="institution"
													required
													placeholder="Your institution name"
													className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-3 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:border-gray-300"
												/>
											</div>
											<ValidationError 
												prefix="Institution" 
												field="institution"
												errors={state.errors}
												className="text-red-500 text-sm mt-1"
											/>
										</div>
										
										<div className="form-group">
											<label htmlFor="role" className="form-label block mb-2">
												<span className="text-sm font-bold text-gray-800">Your Role/Title</span>
											</label>
											<div className="relative">
												<input
													type="text"
													id="role"
													name="role"
													placeholder="Your role or title"
													className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-3 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:border-gray-300"
												/>
											</div>
											<ValidationError 
												prefix="Role" 
												field="role"
												errors={state.errors}
												className="text-red-500 text-sm mt-1"
											/>
										</div>
										
										<div className="form-group">
											<label htmlFor="message" className="form-label block mb-2">
												<span className="text-sm font-bold text-gray-800 leading-relaxed">
													Any concerns, opportunities or frustrations you'd like to share that will help your 
													<span className="text-primary font-extrabold"> SME add as much value as possible</span> to your 
													<span className="text-green-600 font-extrabold"> free report</span>?
												</span>
												<span className="text-xs text-gray-500 ml-2 font-medium"> (Optional)</span>
											</label>
											<div className="relative">
												<textarea
													id="message"
													name="message"
													rows="4"
													placeholder="Share any specific concerns, opportunities, or frustrations that would help us provide the most valuable insights for your institution..."
													className="form-input w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-3 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-200 bg-white shadow-sm hover:border-gray-300 resize-vertical min-h-[100px]"
												/>
											</div>
											<ValidationError 
												prefix="Message" 
												field="message"
												errors={state.errors}
												className="text-red-500 text-sm mt-1"
											/>
										</div>
										
										<div className="pt-2">
											<button 
												type="submit" 
												disabled={state.submitting}
												className={`btn btn-primary w-full py-4 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-200 flex items-center justify-center space-x-2 ${state.submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
											>
												{state.submitting ? (
													<>
														<span>Sending...</span>
													</>
												) : (
													<>
														<span>Request Your Personalized Report</span>
													</>
												)}
											</button>
										</div>
										
										<div className="text-center pt-4">
											<div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-lg mx-auto">
												<p className="text-xs text-gray-600 font-medium text-center">
													<span className="text-green-600 mr-1">🔒</span>
													Your information is <span className="font-bold text-green-700">never shared</span> with any 3rd party. 
													It is secure and will only be used to provide you with the information you are requesting.
												</p>
											</div>
										</div>
									</form>
								)}
								</div>
							</div>
						</div>
						{/* <div className="mb-6">
							<h2 className="text-xl font-semibold mb-2">Potential Next Steps</h2>
							<div className="space-y-4">
								{categoryItems.map(({ key, title, advice, band, allAdvice }) => (
									<AdviceCard key={key} title={title} band={band} mainText={advice} allAdvice={allAdvice} />
								))}
							</div>
						</div> */}
						
						{/* <pre style={{ background: 'var(--bg-accent)', padding: '1rem', borderRadius: 'var(--border-radius)', overflow: 'auto', maxHeight: '50vh' }}>
							{JSON.stringify({ results, breakdown, categoryBreakdown, total, min, max }, null, 2)}
						</pre> */}
						<div className="flex justify-between items-center mt-6">
							<button className="btn btn-outline" onClick={goBackToForm}>Back to Form</button>
							<Link to="/" className="btn btn-primary">Home</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default Results;
